/**
 * archetype.js — Single source of truth for agent + workflow classification
 * in the frontend (G388).
 *
 * Pre-G388 there were at least three parallel `deriveArchetype()` functions
 * in the codebase — in CanvasPageV3.jsx (twice), WorkflowIntelligencePageV2.jsx,
 * and ad-hoc one-liners scattered across other pages. Each one used a
 * slightly different subset of input fields and order of precedence, so they
 * drifted (some checked `a.system`, others `a.isSystemAgent`, others
 * `a.kind === 'system'`).
 *
 * Post-V177 the authoritative classification lives on `workflow.kind` (the
 * CHECK-constrained enum column added by V177) and `agent.archetype`. The
 * helpers below normalise read paths so every UI surface gets the same
 * answer.
 */

/* The 4 agent archetypes. */
export const AGENT_ARCHETYPES = ['Let', 'AutoLet', 'SysLet', 'ExteLet'];

/* The 6 workflow kinds (matches V177 DB CHECK + Workflow.Kind in Java). */
export const WORKFLOW_KINDS = [
  'standalone',
  'native',
  'let_canvas',
  'autolet_ceg',
  'autolet_sandbox',
  'syslet_canvas',
];

/**
 * Returns the agent's archetype. Always prefer the explicit `archetype`
 * field when present (the backend now stamps it from the agent-creation
 * flow); fall back to legacy heuristics for old rows.
 */
export function deriveAgentArchetype(agent) {
  if (!agent) return null;
  if (agent.archetype && AGENT_ARCHETYPES.includes(agent.archetype)) return agent.archetype;

  if (agent.isSystemAgent === true
      || agent.system === true
      || agent.kind === 'system') return 'SysLet';

  if (agent.agentClassification === 'EXTERNAL'
      || agent.isExternal === true
      || agent.gel === true) return 'ExteLet';

  if (agent.isRunbookManaged === true
      || (typeof agent.name === 'string' && agent.name.startsWith('AutoLets-'))) return 'AutoLet';

  return 'Let';
}

/**
 * Returns the workflow's kind. Reads `workflow.kind` (V177 authoritative
 * column) directly when present. Falls back to heuristics for pre-V177
 * rows that haven't been backfilled.
 */
export function deriveWorkflowKind(workflow) {
  if (!workflow) return null;
  if (workflow.kind && WORKFLOW_KINDS.includes(workflow.kind)) return workflow.kind;

  /* Legacy fallback — V177 backfill should have caught all of these. */
  if (workflow.isCanonical === true) return 'autolet_ceg';
  if (workflow.isSandbox === true) return 'autolet_sandbox';
  if (workflow.isSystem === true) return 'syslet_canvas';
  if (workflow.connectorType
      || workflow.discoveredWorkflowId
      || (typeof workflow.name === 'string' && workflow.name.startsWith('[Native]'))) {
    return 'native';
  }
  return 'standalone';
}

/**
 * Returns the archetype to display for a workflow row in inventory /
 * intelligence dashboards. Maps workflow.kind → display archetype.
 * Used by WorkflowIntelligencePageV2 facet filters.
 */
export function deriveWorkflowArchetype(workflow) {
  const kind = deriveWorkflowKind(workflow);
  switch (kind) {
    case 'autolet_ceg':
    case 'autolet_sandbox':
      return 'AutoLet';
    case 'syslet_canvas':
      return 'SysLet';
    case 'let_canvas':
      return 'Let';
    case 'native':
    case 'standalone':
    default:
      /* For standalone / native, archetype is informational at best.
       * Default to Let display when an agent linkage exists, otherwise
       * 'Standalone' (introduced as a 5th UI bucket — facets need to
       * be extended). */
      if (workflow?.agentId) return 'Let';
      return 'Standalone';
  }
}

/**
 * True when this workflow's canvas is fully IMMUTABLE because of its kind
 * alone (autolet_ceg, syslet_canvas). Independent of Let phase locks —
 * those are computed separately from agent.phase.
 *
 * V221 — NOT extended to include {@code standalone_system}. Platform
 * utility workflows (scheduler kickers, sweepers, enforcement entry points)
 * are mutable by SuperAdmin, so they are NOT immutable in the
 * GOLDEN_CEG_RULES §1 sense. The SuperAdmin-only lock for those is enforced
 * by {@code isStandaloneSystemAdminOnly} inside {@code CanvasPageV3.jsx},
 * which composes with this helper via {@code isAutoLetCegReadOnly}.
 *
 * NOTE: This helper currently has no callers — the live gate is computed
 * inline in {@code CanvasPageV3.jsx:isAutoLetCegReadOnly}. Kept exported
 * as a dedupe target for future cleanup.
 */
export function isCanvasImmutableByKind(workflow) {
  const kind = deriveWorkflowKind(workflow);
  return kind === 'autolet_ceg' || kind === 'syslet_canvas';
}

/**
 * True when reverse-sync (canvas → agent.skills) should fire for this
 * workflow on Save. Only let_canvas-kind workflows reverse-sync.
 */
export function isReverseSyncEligible(workflow) {
  return deriveWorkflowKind(workflow) === 'let_canvas';
}
